import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card.tsx'
import { Button } from '@/app/common/components/button/Button.tsx'
import { Input } from '@/app/common/components/input/Input.tsx'
import Label from '@/app/common/components/Label'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { Switch } from '@/app/common/components/Switch'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import type { NonGradeModifierViewProps } from '../modifiers/types'
import { useUpdateNonGradeModifier } from '@/queries/purse_configs/useUpdateNonGradeModifier'
import { PURSE_KEYS } from '@/queries/program_purses/keys'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/shad/ui/toast/use-toast.ts'

const nonGradeModifierSchema = z.object({
  baseAmount: z.coerce
    .number()
    .min(0, 'Amount must be greater than or equal to 0'),
  isActive: z.boolean(),
})

type NonGradeModifierFormData = z.infer<typeof nonGradeModifierSchema>

export default function NonGradeModifierView({
  modifierId,
  modifierTypeId,
  modifierCode,
  modifierName,
  baseAmount,
  isActive,
  partialPurseData,
  onBack,
}: NonGradeModifierViewProps) {
  const updateModifierMutation = useUpdateNonGradeModifier()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const defaultValues = {
    baseAmount: baseAmount,
    isActive: isActive,
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<NonGradeModifierFormData>({
    resolver: zodResolver(nonGradeModifierSchema),
    values: defaultValues,
  })

  const isSubmitting = updateModifierMutation.status === 'pending'

  const onSubmit = async (data: NonGradeModifierFormData) => {
    updateModifierMutation.mutate(
      {
        params: {
          programId: partialPurseData.programId,
          purseId: partialPurseData.id,
        },
        payload: {
          id: modifierId,
          modifier_type_id: modifierTypeId,
          base_amount: data.baseAmount,
          is_active: data.isActive,
        },
      },
      {
        onSuccess: () => {
          reset(data)
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSE_DETAILS(
              partialPurseData.id.toString(),
              partialPurseData.programId
            ),
          })
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSES(partialPurseData.programId),
          })
          toast({
            title: 'Success',
            description: `${modifierName} updated successfully.`,
          })
        },
        onError: (error) => {
          toast({
            title: 'Update failed',
            description: error.message ?? 'Unable to update modifier.',
          })
        },
      }
    )
  }

  return (
    <div className="w-full px-3 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <form
          id={`${modifierCode}-modifier-form`}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Card>
            <CardHeader>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  {modifierName}
                </h3>
                <div className="mt-1 text-sm text-gray-700">
                  <p>
                    Configure the base amount and toggle availability for this
                    modifier.
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-1.5">
                  <Label htmlFor={`${modifierCode}-is-active`} weight="medium">
                    Modifier Status
                  </Label>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-600">
                          {field.value ? 'Active' : 'Inactive'}
                        </span>
                        <Switch
                          id={`${modifierCode}-is-active`}
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </div>
                    )}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor={`${modifierCode}-base`} weight="medium">
                    Amount
                  </Label>
                  <div className="relative">
                    <span
                      aria-hidden
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500"
                    >
                      $
                    </span>
                    <Controller
                      name="baseAmount"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          id={`${modifierCode}-base`}
                          type="number"
                          inputMode="decimal"
                          min={0}
                          step="0.01"
                          className="pl-6"
                          placeholder="0"
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      )}
                    />
                  </div>
                  {errors.baseAmount && (
                    <FormErrorMessage message={errors.baseAmount.message} />
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-start gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onBack}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <SubmitButton
                  status={updateModifierMutation.status}
                  form={`${modifierCode}-modifier-form`}
                  variant="primary"
                  size="sm"
                  className="w-auto"
                  buttonTxt="Save Changes"
                  submittingText="Saving..."
                  forceDisabled={!isDirty || isSubmitting}
                />
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  )
}
